import { useReducer, useEffect, useContext, useCallback } from 'react'
import {
    UniversalDataTable,
    UniversalModal,
    UniversalToolBar,
    UniversalLoadingCircle,
    UniversalCookieCrumb,
    DataGridLayout,
    Grid,
} from '../../UniversalComponents'
import {
    permissionsCheck,
    handleActivation,
    handleDeletion,
    standardColumnMapper,
    useDebounce,
} from '../../../utilities/functionTools'
import {
    Archive as ArchiveIcon,
    ArrowOutward as ArrowOutwardIcon,
    AddToQueue as AddToQueueIcon,
    Restore,
    DeleteForever,
    Difference as DifferenceIcon,
} from '@mui/icons-material'
import {
    getDevices,
    deactivateDevices,
    deleteDevice,
    getDevicesWithLimiter,
} from '../../../apiRoutes'
import { usePermissions, useAuth } from '../../../context/AuthenticationContext'
import { useMinimizer } from '../../../context/MinimizeContext'
import { ToastMessage } from '../../../context/ToastPopUpContext'
import MultipleDeviceUploader from './MultipleDeviceUpload'
import DevicesForm from './DevicesForm'

const today = new Date().toISOString().substring(0, 10)

const Devices = () => {
    const setToastMessage = useContext(ToastMessage)

    const currentUser = useAuth()
    const { setUser } = useAuth()
    const { username, userProfile, activeManufacturer, userPreferences, _id } =
        currentUser.auth
    const { superAdmin, userProfilePermissions } = userProfile

    const defaultState = {
        open: false,
        openDevices: false,
        row: {},
        data: [],
        viewDeactivated: false,
        formCache: {},
        activateData: [],
        tabName: '',
        loading: false,
        paginationModel: { page: 0, pageSize: 50 },
        apiSearch: '',
        rowCount: 0,
        noResults: false,
        devices: {
            columns: [
                {
                    field: 'manufacturerName',
                    headerName: 'MANUFACTURER',
                    width: 300,
                },
                { field: 'iccid', headerName: 'ICCID', width: 150 },
                { field: 'fullHin', headerName: 'HIN', width: 150 },
                { field: 'partNumber', headerName: 'PART NUMBER', width: 150 },
                {
                    field: 'deviceSerial',
                    headerName: 'DEVICE SERIAL',
                    width: 400,
                },
            ],
            density: 'standard',
        },
        density:
            userPreferences?.dataTableColumns?.devices?.density || 'standard',
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const { setMinimized } = useMinimizer()

    const {
        open,
        openDevices,
        row,
        data,
        activateData,
        viewDeactivated,
        formCache,
        devices,
        density,
        tabName,
        loading,
        paginationModel,
        apiSearch,
        rowCount,
        noResults,
    } = state

    const debouncedSearchQuery = useDebounce(apiSearch, 1000)

    const onApiSearch = useCallback((query: any) => {
        setState({
            apiSearch: query,
        })
        if (query !== null) {
            setState({ paginationModel: { ...paginationModel, page: 0 } })
        }
    }, [])

    const getData = async (active) => {
        setState({ loading: true })
        let deviceData: any
        try {
            const devices = await getDevicesWithLimiter({
                active,
                page: paginationModel?.page + 1,
                limit: 50,
                filter: debouncedSearchQuery
                    ? JSON.stringify(debouncedSearchQuery)
                    : undefined,
            })

            deviceData = Array.isArray(devices?.devices) ? devices.devices : []
            const total = Number(devices?.total) || 0

            if (
                deviceData?.length === 0 &&
                total === 0 &&
                debouncedSearchQuery !== null
            ) {
                setToastMessage(
                    'No Devices found - Please Try another Search',
                    'info'
                )
                setState({
                    apiSearch: null,
                    paginationModel: { page: 0, pageSize: 50 },
                    rowCount: total,
                    loading: false,
                    noResults: true,
                })
                return
            }
            deviceData = standardColumnMapper(
                defaultColumns,
                devices?.devices
            ).map((item: any) => ({
                ...item,
                id: item?._id,
            }))

            setState({
                data: deviceData,
                rowCount: total,
                loading: false,
                noResults: false,
            })
        } catch (err: any) {
            let errorMessage = 'An unknown error occurred'

            if (err.response?.data?.message) {
                errorMessage = err.response.data.message
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error
            } else if (err.message) {
                errorMessage = err.message
            }
            setState({ data: [], rowCount: 0, users: [] })
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const collectColumnData = () => {
        const devices = userPreferences?.dataTableColumns?.devices?.columns

        if (Array.isArray(devices)) {
            const mapColumns = devices.map((device) => ({
                field: device?.field,
                headerName: device?.headerName,
                width: device?.width,
                isVisible: device?.isVisible,
            }))

            const format = { columns: mapColumns }

            setState({ ...state, devices: format })
        }
    }

    const submitColumnData = async () => {
        try {
            setUser({
                key: 'devices',
                value: devices,
                type: 'columnData',
            })
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleActivateData = () => {
        handleActivation({
            activateData,
            data,
            returnKey: 'deviceSerial',
            setState,
            getData,
            route: deactivateDevices,
            viewDeactivated,
            setToastMessage,
            page: 'devices',
        })
    }

    const handleDeletionData = () => {
        handleDeletion({
            activateData,
            data,
            returnKey: 'deviceSerial',
            setState,
            getData,
            route: deleteDevice,
            viewDeactivated,
            setToastMessage,
            page: 'devices',
        })
    }

    const handleDeletionCheck = () => {
        setToastMessage(
            `Are you sure you want to delete these Devices?`,
            'warning',
            handleDeletionData
        )
    }

    const setDeactivatedView = () => {
        setState({ viewDeactivated: !viewDeactivated })
    }

    useEffect(() => {
        collectColumnData()
        getData(true)
    }, [])

    useEffect(() => {
        setState({ paginationModel: { page: 0, pageSize: 50 } })
        getData(!viewDeactivated)
    }, [viewDeactivated, activeManufacturer])

    useEffect(() => {
        if (noResults) {
            return
        } else {
            getData(true)
        }
    }, [paginationModel?.page, debouncedSearchQuery])

    const defaultColumns = [
        'deviceSerial',
        'manufacturer.manufacturerName',
        'iccid',
        'boat.fullHin',
        'part.partNumber',
        'id',
        'provider',
        'part.partDescription',
        'part._id',
        'boat.shortHin',
        'archived',
        'active',
        'boat.nickname',
        'boat.provisionalBoat',
    ]

    const visibleColumns = [
        'deviceSerial',
        'manufacturerName',
        'iccid',
        'fullHin',
        'partNumber',
    ]

    const customColumnNames = [
        'Device Serial',
        'Manufacturer',
        'ICCID',
        'HIN',
        'Part Number',
    ]

    const setModalVisbility = async (tableRow: any, newData: any) => {
        if (tableRow?.deviceSerial) {
            setState({ row: tableRow, open: !open })
        } else {
            setState({
                row: defaultState.row,
                open: !open,
            })
        }
        if (newData !== undefined) {
            return getData(true)
        }
    }

    const minimizeModal = () => {
        setMinimized(true, {
            title: formCache.deviceSerial
                ? `Edit: ${formCache.deviceSerial}`
                : 'Add New Device',
            link: 'admin/devices',
            data: formCache.id ? formCache : { ...formCache, id: 'newDevice' },
            id: formCache.id ? formCache.id : 'newDevice',
        })
        setState({ open: false, row: defaultState.row })
    }

    const buttons = [
        {
            buttonName: 'Add New Device',
            buttonIcon: <AddToQueueIcon />,
            buttonFunction: () => setState({ open: !open }),
            disabled: permissionsCheck(userProfilePermissions, 'Devices'),
        },
        {
            buttonName: 'Provisioning',
            buttonIcon: <DifferenceIcon />,
            buttonFunction: () => setState({ openDevices: !openDevices }),
            disabled: permissionsCheck(userProfilePermissions, 'Super Admin'),
            display: permissionsCheck(userProfilePermissions, 'Super Admin')
                ? 'none'
                : null,
        },
        {
            buttonName: !viewDeactivated ? 'Deactivate' : 'Activate',
            buttonIcon: <ArchiveIcon />,
            buttonFunction: handleActivateData,
            disabled: permissionsCheck(userProfilePermissions, 'Devices'),
        },
        {
            buttonName: viewDeactivated
                ? 'View Active Devices'
                : 'View Deactivated Devices',
            buttonIcon: <Restore />,
            buttonFunction: setDeactivatedView,
        },
        {
            buttonName: 'Delete',
            buttonIcon: <DeleteForever />,
            buttonFunction: handleDeletionCheck,
            disabled: permissionsCheck(userProfilePermissions, 'Super Admin'),
            display: !viewDeactivated ? 'none' : null,
        },
        // {
        //     buttonName: 'Export',
        //     buttonIcon: <ArrowOutwardIcon />,
        //     buttonFunction: () => {},
        // },
    ]

    const handleUpdatedColumnsChange = (updatedColumns) => {
        const format = updatedColumns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
            width: col.width,
            isVisible: col.isVisible,
        }))

        const setDensity = state.density

        const setDevices = { columns: format, density: setDensity }

        setState({
            devices: setDevices,
        })
    }

    const handleUpdatedVisibility = (columnVisibilityModel) => {
        const updatedVisibility = devices?.columns.reduce((acc, col) => {
            if (col.field in columnVisibilityModel) {
                acc[col.field] = {
                    ...col,
                    isVisible: columnVisibilityModel[col.field],
                }
            } else {
                acc[col.field] = col
            }

            return acc
        }, {})

        const updatedColumns = Object.values(updatedVisibility)

        const setDensity = state.density

        const setDevices = { columns: updatedColumns, density: setDensity }

        setState({ ...state, devices: setDevices })
    }

    const handleDensityChange = (newDensity) => {
        setState({
            density: newDensity,
            devices: {
                ...devices,
                density: newDensity,
            },
        })
    }

    return (
        <Grid container>
            <DataGridLayout
                cookieCrumbs={<UniversalCookieCrumb customTitle={tabName} />}
                toolbar={<UniversalToolBar buttons={buttons} />}
                dataGrid={
                    <UniversalLoadingCircle
                        data={data}
                        reloadFunction={!viewDeactivated ? getData : () => {}}
                        customTimeoutMessage={
                            !!viewDeactivated &&
                            'No Deactivated Devices Available'
                        }
                    >
                        <UniversalDataTable
                            data={data}
                            apiDensity={density}
                            apiColumns={devices?.columns}
                            savePreferences={() => submitColumnData()}
                            visibleColumns={[]}
                            customColumnNames={customColumnNames}
                            getRowData={(tableRow) =>
                                setModalVisbility(tableRow, undefined)
                            }
                            onRowSelectionModelChange={(newSelection) => {
                                setState({
                                    activateData: newSelection,
                                })
                            }}
                            onUpdatedColumnsChange={handleUpdatedColumnsChange}
                            getColumnHeaderVisibility={handleUpdatedVisibility}
                            onDensityChange={handleDensityChange}
                            rowCount={rowCount}
                            paginationModel={paginationModel}
                            loading={loading}
                            paginationMode={'server'}
                            pageSizeOptions={[50]}
                            setPaginationModel={(data) => {
                                setState({ paginationModel: data })
                            }}
                            onApiSearch={(text) => {
                                setState({ noResults: false })
                                onApiSearch(text)
                            }}
                        />
                    </UniversalLoadingCircle>
                }
            />
            <UniversalModal
                title={`${
                    row.deviceSerial
                        ? `Device Serial : ${row.deviceSerial}`
                        : 'Configure A Telematics Device'
                }`}
                timeStamp={`${
                    row.updatedBy
                        ? ` ${
                              row.updatedBy
                                  ? ` Modified By ${row.updatedBy}`
                                  : ''
                          } ${
                              row.updatedAt
                                  ? `| Modified Date ${row.updatedAt.substring(
                                        0,
                                        10
                                    )}`
                                  : ''
                          }  `
                        : ` Modified By: ${username} | Modified Date: ${today} `
                }`}
                open={open}
                onClose={(tableRow) => setModalVisbility(tableRow, undefined)}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
            >
                <DevicesForm
                    formTitle="Configure A Telematics Device"
                    row={row}
                    onClose={(tableRow: any, newData: any) =>
                        setModalVisbility(tableRow, newData)
                    }
                    saveCache={(data: any) => setState({ formCache: data })}
                    minimizeModal={minimizeModal}
                    permissionCheck={permissionsCheck(
                        userProfilePermissions,
                        'Devices'
                    )}
                    quickStart={false}
                />
            </UniversalModal>

            <UniversalModal
                title={'Provision Multiple Devices'}
                open={openDevices}
                onClose={() => setState({ openDevices: false })}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
            >
                <MultipleDeviceUploader
                    closeModal={() => setState({ openDevices: false })}
                    onClose={() => getData(true)}
                />
            </UniversalModal>
        </Grid>
    )
}

export default Devices
